import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, Copy, Loader2, Lock, MoreHorizontal, Pencil, Plus, ShieldCheck, SlidersHorizontal, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import * as repo from '../data/rolesRepository';
import { RoleFormDialog, type RoleFormMode } from '../components/RoleFormDialog';
import { usePermissionCatalog, useRolesMutation, useRolesOverview } from '../hooks/useRoles';
import type { RoleFormValues, RoleOverview } from '../types';

export function RoleListPage() {
  const navigate = useNavigate();
  const { data: roles = [], isLoading, isError, refetch } = useRolesOverview();
  const { data: catalog = [] } = usePermissionCatalog();
  const [form, setForm] = useState<RoleFormMode | null>(null);
  const [deleting, setDeleting] = useState<RoleOverview | null>(null);

  const create = useRolesMutation(
    ({ values, copyFrom }: { values: RoleFormValues; copyFrom?: string }) => repo.createRole(values, copyFrom),
    { success: 'Rol creado' },
  );
  const update = useRolesMutation(
    ({ id, values }: { id: string; values: RoleFormValues }) => repo.updateRole(id, values),
    { success: 'Rol actualizado' },
  );
  const remove = useRolesMutation((id: string) => repo.deleteRole(id), { success: 'Rol eliminado' });

  const submitForm = (values: RoleFormValues) => {
    if (!form) return;
    if (form.kind === 'edit') {
      update.mutate({ id: form.role.id, values }, { onSuccess: () => setForm(null) });
      return;
    }
    const copyFrom = form.kind === 'duplicate' ? form.source.id : undefined;
    create.mutate({ values, copyFrom }, {
      onSuccess: (id) => {
        setForm(null);
        navigate(`/roles/${id}`);
      },
    });
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold"><ShieldCheck className="size-6" /> Roles y permisos</h1>
          <p className="text-sm text-muted-foreground">
            Define qué puede hacer cada rol. Los roles se asignan a las personas desde Usuarios.
          </p>
        </div>
        <Button onClick={() => setForm({ kind: 'create' })}><Plus className="mr-2 size-4" /> Nuevo rol</Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>
      ) : isError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center text-sm text-muted-foreground">
            <AlertCircle className="size-6 text-destructive" />
            No se pudieron cargar los roles.
            <Button variant="outline" size="sm" onClick={() => void refetch()}>Reintentar</Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rol</TableHead>
                  <TableHead className="w-48">Permisos</TableHead>
                  <TableHead className="w-24 text-right">Usuarios</TableHead>
                  <TableHead className="w-12"><span className="sr-only">Acciones</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id} className="cursor-pointer" onClick={() => navigate(`/roles/${role.id}`)}>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/roles/${role.id}`}
                          className="font-medium hover:underline focus-visible:underline focus-visible:outline-none"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {role.name}
                        </Link>
                        {role.permissions_locked && (
                          <Badge variant="secondary" className="gap-1"><Lock className="size-3" /> Acceso total</Badge>
                        )}
                        {role.is_system && !role.permissions_locked && <Badge variant="secondary">Sistema</Badge>}
                        {!role.is_active && <Badge variant="outline">Inactivo</Badge>}
                      </div>
                      <p className="font-mono text-xs text-muted-foreground">{role.code}</p>
                      {role.description && <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{role.description}</p>}
                    </TableCell>
                    <TableCell>
                      <PermissionCount count={role.permission_count} total={catalog.length} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{role.user_count}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <RoleActions
                        role={role}
                        onPermissions={() => navigate(`/roles/${role.id}`)}
                        onEdit={() => setForm({ kind: 'edit', role })}
                        onDuplicate={() => setForm({ kind: 'duplicate', source: role })}
                        onDelete={() => setDeleting(role)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <RoleFormDialog
        mode={form}
        onOpenChange={(open) => !open && setForm(null)}
        busy={create.isPending || update.isPending}
        onSubmit={submitForm}
      />

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar el rol «{deleting?.name}»?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminan el rol y sus permisos. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleting && remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PermissionCount({ count, total }: { count: number; total: number }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className="space-y-1">
      <p className="text-sm tabular-nums">{count}{total ? <span className="text-muted-foreground"> / {total}</span> : null}</p>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

interface RoleActionsProps {
  role: RoleOverview;
  onPermissions: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function RoleActions({ role, onPermissions, onEdit, onDuplicate, onDelete }: RoleActionsProps) {
  const deleteBlocked = role.is_system
    ? 'Los roles de sistema no se eliminan'
    : role.user_count > 0
      ? `Asignado a ${role.user_count} usuario(s)`
      : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Acciones de ${role.name}`}>
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onPermissions}>
          <SlidersHorizontal className="mr-2 size-4" /> {role.permissions_locked ? 'Ver permisos' : 'Editar permisos'}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onEdit}><Pencil className="mr-2 size-4" /> Editar datos</DropdownMenuItem>
        <DropdownMenuItem onSelect={onDuplicate}><Copy className="mr-2 size-4" /> Duplicar</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={!!deleteBlocked}
          onSelect={onDelete}
          className="text-destructive focus:text-destructive"
        >
          <Trash2 className="mr-2 size-4" />
          <div>
            Eliminar
            {deleteBlocked && <p className="text-xs text-muted-foreground">{deleteBlocked}</p>}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
